// The pub map index, loaded a shard at a time (see schema.ts): meta.json and
// overview.json up front, shards/<key>.json for the cells in view, and
// search.json only when someone searches or follows a shared link.
//
// A PubStore holds every shard loaded so far. Adding shards makes a new store
// with the old pubs at the same indices, so an index stays valid for the
// whole session.

import {
  DATASET_VERSION,
  Flag,
  NATIONS,
  shardKey,
  shardKeysIn,
  tileFor,
  type DatasetMeta,
  type DetailTileFile,
  type Nation,
  type OverviewFile,
  type PubDetails,
  type PubIndexFile,
  type SearchFile,
  type ShardFile,
} from './schema';
import type { Feature, FeatureCollection, Point } from 'geojson';
import { NO_FILTERS, passes, type Filters } from '../lib/filters';
import { decodeMask } from '../lib/openingHoursCore';

export interface PubStore {
  count: number;
  generated: string;
  ids: string[];
  names: string[];
  lat: number[];
  lon: number[];
  flags: number[];
  /** Index into chains + 1; 0 = independent/unknown. */
  chain: number[];
  chains: string[];
  nation: number[];
  tiles: Set<string>;
  indexOf: Map<string, number>;
  /** Weekly opening-hours masks for pubs whose hours parse (see schema). */
  hours: Map<number, Uint8Array>;
  /** Shard keys already in this store. */
  shards: Set<string>;
}

/** Decode a full columnar index file (delta-encoded coordinates) into a PubStore. */
export function decodeIndex(file: PubIndexFile): PubStore {
  if (file.v !== DATASET_VERSION) {
    throw new Error(`Unsupported dataset version ${String(file.v)}`);
  }
  const store = emptyStore({
    v: file.v,
    gen: file.gen,
    n: 0,
    hash: '',
    chains: file.chains,
    tiles: file.tiles,
    shards: [],
  });
  return appendShard(store, '', file);
}

/** A store with no pubs yet (just the dataset-wide lists). */
export function emptyStore(meta: DatasetMeta): PubStore {
  return {
    count: 0,
    generated: meta.gen,
    ids: [],
    names: [],
    lat: [],
    lon: [],
    flags: [],
    chain: [],
    chains: meta.chains,
    nation: [],
    tiles: new Set(meta.tiles),
    indexOf: new Map(),
    hours: new Map(),
    shards: new Set(),
  };
}

/** A new store: `store` plus one shard's pubs (appended, so old indices stay put). */
export function appendShard(store: PubStore, key: string, shard: ShardFile): PubStore {
  const next: PubStore = {
    ...store,
    ids: [...store.ids],
    names: [...store.names],
    lat: [...store.lat],
    lon: [...store.lon],
    flags: [...store.flags],
    chain: [...store.chain],
    nation: [...store.nation],
    indexOf: new Map(store.indexOf),
    hours: new Map(store.hours),
    shards: new Set(store.shards).add(key),
  };
  let la = 0;
  let lo = 0;
  const base = store.count;
  for (let i = 0; i < shard.n; i++) {
    la += shard.lat[i] ?? 0;
    lo += shard.lon[i] ?? 0;
    const id = shard.id[i] ?? '';
    if (next.indexOf.has(id)) continue; // already loaded
    next.indexOf.set(id, next.ids.length);
    next.ids.push(id);
    next.names.push(shard.name[i] ?? '');
    next.lat.push(la / 1e5);
    next.lon.push(lo / 1e5);
    next.flags.push(shard.f[i] ?? 0);
    next.chain.push(shard.c[i] ?? 0);
    next.nation.push(Number(shard.nat[i] ?? 0));
  }
  (shard.ohi ?? []).forEach((local, k) => {
    const mask = decodeMask(shard.ohm?.[k] ?? '');
    const index = next.indexOf.get(shard.id[local] ?? '');
    if (mask && index !== undefined && index >= base) next.hours.set(index, mask);
  });
  next.count = next.ids.length;
  return next;
}

async function getJson<T>(url: string, what: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${what} failed to load (${response.status})`);
  return response.json() as Promise<T>;
}

/** Fetch once; on failure the next call retries. */
function once<T>(load: () => Promise<T>): () => Promise<T> {
  let promise: Promise<T> | null = null;
  return () => {
    promise ??= load().catch((error: unknown) => {
      promise = null;
      throw error;
    });
    return promise;
  };
}

export const loadMeta = once(async () => {
  const meta = await getJson<DatasetMeta>('/data/meta.json', 'Pub data');
  if (meta.v !== DATASET_VERSION) throw new Error(`Unsupported dataset version ${String(meta.v)}`);
  return meta;
});

export const loadOverview = once(() => getJson<OverviewFile>('/data/overview.json', 'Pub data'));

export const loadSearch = once(() => getJson<SearchFile>('/data/search.json', 'Pub search'));

const shardCache = new Map<string, Promise<ShardFile>>();

export function loadShard(key: string): Promise<ShardFile> {
  let shard = shardCache.get(key);
  if (!shard) {
    shard = getJson<ShardFile>(`/data/shards/${key}.json`, 'Pub data');
    shardCache.set(key, shard);
    shard.catch(() => shardCache.delete(key));
  }
  return shard;
}

/** Shards covering a box plus a margin (a fraction of its size on each side). */
export function shardsFor(
  meta: DatasetMeta,
  box: { south: number; west: number; north: number; east: number },
  margin = 0.25,
): string[] {
  const dLat = (box.north - box.south) * margin;
  const dLon = (box.east - box.west) * margin;
  const exists = new Set(meta.shards);
  return shardKeysIn(box.south - dLat, box.west - dLon, box.north + dLat, box.east + dLon).filter(
    (key) => exists.has(key),
  );
}

/** Load any of these shards not yet in the store; resolves to the bigger store (or the same one). */
export async function withShards(store: PubStore, keys: readonly string[]): Promise<PubStore> {
  const missing = keys.filter((key) => !store.shards.has(key));
  if (missing.length === 0) return store;
  const shards = await Promise.all(missing.map((key) => loadShard(key)));
  return missing.reduce((s, key, i) => appendShard(s, key, shards[i] as ShardFile), store);
}

/** The shard a point is in. */
export const shardOf = shardKey;

/** The overview as map points, counting only what the filters allow (confirmed, or all). */
export function overviewGeoJSON(
  overview: OverviewFile,
  filters: Filters = NO_FILTERS,
): FeatureCollection<Point> {
  const features: Feature<Point>[] = [];
  overview.n.forEach((n, i) => {
    const k = filters.confirmed ? (overview.g[i] ?? 0) : n;
    if (k === 0) return;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [(overview.lon[i] ?? 0) / 1000, (overview.lat[i] ?? 0) / 1000],
      },
      properties: { k },
    });
  });
  return { type: 'FeatureCollection', features };
}

/** Everything the UI needs about one pub from the index (no network). */
export interface PubSummary {
  index: number;
  id: string;
  name: string;
  lat: number;
  lon: number;
  flags: number;
  chain: string | null;
  nation: Nation;
  /** Weekly opening-hours mask, when the pub's hours parse. */
  hours?: Uint8Array;
}

export function summary(store: PubStore, index: number): PubSummary {
  const chainIndex = store.chain[index] ?? 0;
  return {
    index,
    id: store.ids[index] ?? '',
    name: store.names[index] ?? '',
    lat: store.lat[index] ?? 0,
    lon: store.lon[index] ?? 0,
    flags: store.flags[index] ?? 0,
    chain: chainIndex > 0 ? (store.chains[chainIndex - 1] ?? null) : null,
    nation: NATIONS[store.nation[index] ?? 0] ?? 'ENG',
    ...(store.hours.has(index) ? { hours: store.hours.get(index) as Uint8Array } : {}),
  };
}

/** A beer garden or outdoor seating is confirmed, by tag or by an area mapped at the pub. */
export const hasOutdoorSpace = (flags: number) =>
  (flags & (Flag.Garden | Flag.Outdoor | Flag.GardenNearby | Flag.OutdoorNearby)) !== 0;

/**
 * Sort boost for confirmed pubs in "nearest" lists: their squared distance is
 * multiplied by this (0.6 ≈ treating them as 23% closer).
 */
export const CONFIRMED_SORT_BOOST = 0.6;

/** Squared-distance multiplier for a pub (the sort boost). */
export const sortWeight = (store: PubStore, index: number) =>
  hasOutdoorSpace(store.flags[index] ?? 0) ? CONFIRMED_SORT_BOOST : 1;

/**
 * GeoJSON for the map. `g` marks pubs with confirmed outside space for the
 * visual boost. Pubs that fail `filters` are left out entirely.
 */
export function toGeoJSON(
  store: PubStore,
  filters: Filters = NO_FILTERS,
): FeatureCollection<Point> {
  const features: Feature<Point>[] = [];
  for (let i = 0; i < store.count; i++) {
    const flags = store.flags[i] ?? 0;
    if (!passes(flags, filters)) continue;
    const confirmed = hasOutdoorSpace(flags);
    features.push({
      type: 'Feature',
      id: i,
      geometry: { type: 'Point', coordinates: [store.lon[i] ?? 0, store.lat[i] ?? 0] },
      properties: { i, name: store.names[i], g: confirmed ? 1 : 0 },
    });
  }
  return { type: 'FeatureCollection', features };
}

// --- Details (fetched per tile on demand) ---

const tileCache = new Map<string, Promise<DetailTileFile>>();

export const tileUrl = (key: string) => `/data/details/${key || '_'}.json`;

export async function loadDetails(store: PubStore, index: number): Promise<PubDetails> {
  const key = tileFor(store.lat[index] ?? 0, store.lon[index] ?? 0, store.tiles);
  if (key === null) return {};
  let tile = tileCache.get(key);
  if (!tile) {
    tile = fetch(tileUrl(key)).then((response) => {
      if (!response.ok) throw new Error(`Pub details failed to load (${response.status})`);
      return response.json() as Promise<DetailTileFile>;
    });
    tileCache.set(key, tile);
    tile.catch(() => tileCache.delete(key));
  }
  return (await tile)[store.ids[index] ?? ''] ?? {};
}

export interface PubHit {
  id: string;
  name: string;
  lat: number;
  lon: number;
}

/**
 * Name search over every pub (case/accent-insensitive "contains"), nearest
 * first when a point is given, with confirmed outside space nudged up.
 */
export function searchPubNames(
  search: SearchFile,
  query: string,
  near?: { lat: number; lon: number },
  limit = 6,
): PubHit[] {
  const q = fold(query);
  if (q.length < 3) return [];
  const hits: number[] = [];
  for (let i = 0; i < search.id.length; i++) {
    if (fold(search.name[i] ?? '').includes(q)) hits.push(i);
  }
  if (near) {
    const k = Math.cos((near.lat * Math.PI) / 180);
    const d = (i: number) =>
      (((search.lat[i] ?? 0) / 1000 - near.lat) ** 2 +
        (((search.lon[i] ?? 0) / 1000 - near.lon) * k) ** 2) *
      (search.g[i] ? CONFIRMED_SORT_BOOST : 1);
    hits.sort((a, b) => d(a) - d(b));
  }
  return hits.slice(0, limit).map((i) => ({
    id: search.id[i] ?? '',
    name: search.name[i] ?? '',
    lat: (search.lat[i] ?? 0) / 1000,
    lon: (search.lon[i] ?? 0) / 1000,
  }));
}

/** Where a pub is (roughly), from the search file: for deep links. */
export function findPub(search: SearchFile, id: string): PubHit | null {
  const i = search.id.indexOf(id);
  if (i < 0) return null;
  return {
    id,
    name: search.name[i] ?? '',
    lat: (search.lat[i] ?? 0) / 1000,
    lon: (search.lon[i] ?? 0) / 1000,
  };
}

const foldCache = new Map<string, string>();
function fold(text: string): string {
  let folded = foldCache.get(text);
  if (folded === undefined) {
    folded = text
      .normalize('NFKD')
      .replace(/\p{M}/gu, '') // combining marks left by NFKD (accents)
      .toLowerCase()
      .replace(/['’]/g, '');
    if (foldCache.size < 60_000) foldCache.set(text, folded);
  }
  return folded;
}
